/**
 * Fire-and-forget Telemetrie für den DiagnosticBanner.
 *
 * Designprinzipien:
 *  - Eine Übermittlung pro Tab-Session (sessionStorage-Flag) → kein Spam.
 *  - Keine PII: nur Sentinel-Werte, Build-Modus, Browser-Familie, Pfad.
 *  - Best-effort: Fehler werden lautlos verworfen, damit die UI niemals
 *    durch Telemetrie kaputt geht.
 *  - `sendBeacon` bevorzugt (überlebt Tab-Close), Fallback auf `fetch`.
 */
export type TelemetryPayload = {
  bannerTriggered: boolean;
  cssMissing: boolean;
  hasDesignTokens: boolean;
  hiddenWorks: boolean;
  loadedSheets: number;
  buildMode: "dev" | "prod" | "unknown";
  browserFamily: "chrome" | "firefox" | "safari" | "edge" | "brave" | "unknown";
  selfTestTotal: number;
  selfTestFailed: number;
  translated: boolean;
};

const SESSION_FLAG = "diag-telemetry:sent";
const SESSION_ID_KEY = "diag-telemetry:sid";
const ENDPOINT = "/api/public/diagnostic-telemetry";

function getOrCreateSessionId(): string {
  try {
    const existing = window.sessionStorage?.getItem(SESSION_ID_KEY);
    if (existing) return existing;
    const id = crypto.randomUUID().replace(/-/g, "");
    window.sessionStorage?.setItem(SESSION_ID_KEY, id);
    return id;
  } catch {
    // sessionStorage gesperrt (Private Mode) → flüchtige ID, doppelte Events möglich
    return Math.random().toString(36).slice(2, 18);
  }
}

function alreadySent(): boolean {
  try {
    return window.sessionStorage?.getItem(SESSION_FLAG) === "1";
  } catch {
    return false;
  }
}

function markSent() {
  try {
    window.sessionStorage?.setItem(SESSION_FLAG, "1");
  } catch {
    /* ignore */
  }
}

/**
 * Sendet einen Telemetrie-Event. Idempotent pro Session.
 * Gibt true zurück, wenn der Versand initiiert wurde.
 */
export function sendDiagnosticTelemetry(payload: TelemetryPayload): boolean {
  if (typeof window === "undefined" || alreadySent()) return false;
  // Pfad ohne Query — Query kann sensible Tokens enthalten (z. B. ?oauth=...).
  const routePath = window.location?.pathname?.slice(0, 255) ?? null;
  const body = JSON.stringify({
    sessionId: getOrCreateSessionId(),
    routePath,
    ...payload,
  });

  let dispatched = false;
  try {
    if (navigator.sendBeacon) {
      const blob = new Blob([body], { type: "application/json" });
      dispatched = navigator.sendBeacon(ENDPOINT, blob);
    }
  } catch {
    dispatched = false;
  }
  if (!dispatched) {
    // Fallback: keep-alive Fetch, damit auch Tab-Close-Race überlebt wird.
    try {
      void fetch(ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body,
        keepalive: true,
        credentials: "omit",
      }).catch(() => undefined);
      dispatched = true;
    } catch {
      dispatched = false;
    }
  }
  if (dispatched) markSent();
  return dispatched;
}
